import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Dialog, DialogTitle, DialogContent, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import OrgTree from 'react-org-tree';
import axios from 'axios';
import * as common from '../../../commons/common';

// 대시보드 StatCard dot 색상과 동일
const TIER_CONFIG = [
    { accent: '#3b82f6', label: '총괄부서' },
    { accent: '#06b6d4', label: '관리부서' },
    { accent: '#10b981', label: '팀' },
];

// ────────────────────────────────────────────────────────────────
// 노드 – 대시보드 카드 스타일 (흰 배경 + 작은 dot 인디케이터)
// ────────────────────────────────────────────────────────────────
const NodeCard = ({ label, depth }) => {
    if (depth < 0) {
        return (
            <Box sx={{
                backgroundColor: '#1e293b',
                borderRadius: '8px',
                width: 120,
                py: 0.75,
                boxShadow: '0 2px 8px rgba(15,23,42,0.20)',
                textAlign: 'center',
            }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                    {label}
                </Typography>
            </Box>
        );
    }
    const cfg = TIER_CONFIG[depth] || TIER_CONFIG[TIER_CONFIG.length - 1];
    return (
        <Box sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e8edf2',
            width: 120,
            py: 0.75,
            px: 1.2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.08)' },
        }}>
            <Box sx={{
                width: 8, height: 8, borderRadius: '2px',
                backgroundColor: cfg.accent,
                flexShrink: 0,
            }} />
            <Typography sx={{
                fontSize: '0.76rem', fontWeight: 600,
                color: '#1e293b', lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
            }}>
                {label}
            </Typography>
        </Box>
    );
};

// ────────────────────────────────────────────────────────────────
// 조직도 다이얼로그
// ────────────────────────────────────────────────────────────────
const OrgChartDialog = ({ open, onClose }) => {
    const [organizationData, setOrganizationData] = useState(null);
    const containerRef = useRef(null);
    const innerRef = useRef(null);
    const [treeScale, setTreeScale] = useState(1);

    useEffect(() => {
        if (open) {
            setTreeScale(1);
            setOrganizationData(null);
            fetchOrganizationData();
        }
    }, [open]);

    // 데이터 로드 후 트리 자연 너비 측정 → zoom-to-fit 계산
    useEffect(() => {
        if (!organizationData) return;
        const timer = setTimeout(() => {
            const container = containerRef.current;
            const inner = innerRef.current;
            if (!container || !inner) return;
            const available = container.clientWidth;
            const natural = inner.scrollWidth;
            if (natural > available) {
                setTreeScale(available / natural);
            } else {
                setTreeScale(1);
            }
        }, 120);
        return () => clearTimeout(timer);
    }, [organizationData]);

    const fetchOrganizationData = async () => {
        const token = localStorage.getItem('access_token');
        try {
            const response = await axios.get(`${common.getApiUrl()}/dept/all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setOrganizationData(buildTree(response.data));
        } catch (error) {
            console.error('조직 데이터 조회 실패:', error);
        }
    };

    const buildTree = (data) => {
        const map = {};
        data.forEach(dept => { map[dept.deptId] = { ...dept, children: [] }; });
        data.forEach(dept => {
            if (dept.upperDept && dept.upperDept !== 0 && map[dept.upperDept]) {
                map[dept.upperDept].children.push(map[dept.deptId]);
            }
        });
        const roots = data
            .filter(d => !d.upperDept || d.upperDept === 0)
            .map(d => map[d.deptId]);
        return {
            label: <NodeCard label="조직" depth={-1} />,
            children: roots.map(d => toTreeNode(d, 0)),
        };
    };

    const toTreeNode = (dept, depth) => ({
        label: <NodeCard label={dept.deptNm} depth={depth} />,
        key: String(dept.deptId),
        children: (dept.children || []).map(child => toTreeNode(child, depth + 1)),
    });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 12px 48px rgba(0,0,0,0.16)',
                    overflow: 'hidden',
                    width: '90vw',
                    maxWidth: 960,
                    backgroundColor: '#fff',
                }
            }}
        >
            {/* 헤더 */}
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{
                    px: 2.5, py: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: '1px solid #f1f5f9',
                }}>
                    <Box display="flex" alignItems="center" gap={1.2}>
                        <AccountTreeOutlinedIcon sx={{ color: '#1565C0', fontSize: '1.1rem' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                            조직도
                        </Typography>
                        <Box display="flex" gap={1.5} ml={0.5}>
                            {TIER_CONFIG.map((cfg, i) => (
                                <Box key={i} display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{
                                        width: 8, height: 8, borderRadius: '2px',
                                        backgroundColor: cfg.accent,
                                    }} />
                                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 500 }}>
                                        {cfg.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                        <CloseIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* 조직도 트리 */}
            <DialogContent sx={{ p: 0 }}>
                <Box
                    ref={containerRef}
                    sx={{
                        px: 3, py: 3,
                        overflow: 'auto',
                        maxHeight: '75vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        // react-org-tree 기본 스타일 초기화
                        '& .org-tree-node-label .org-tree-node-label-inner': {
                            padding: '0 !important',
                            border: 'none !important',
                            boxShadow: 'none !important',
                            borderRadius: '0 !important',
                            background: 'transparent !important',
                        },
                        '& .org-tree-node-btn': { display: 'none !important' },
                        '& .org-tree': { backgroundColor: 'transparent !important' },
                        '& .org-tree-node::before': { borderTopColor: '#e2e8f0 !important' },
                        '& .org-tree-node::after': { borderLeftColor: '#e2e8f0 !important' },
                        '& .org-tree-node-children::before': { borderLeftColor: '#e2e8f0 !important' },
                    }}
                >
                    {organizationData ? (
                        <Box
                            ref={innerRef}
                            style={{
                                zoom: treeScale,
                                transformOrigin: 'top center',
                                transition: 'zoom 0.2s ease',
                            }}
                        >
                            <OrgTree
                                data={organizationData}
                                horizontal={false}
                                collapsable={false}
                            />
                        </Box>
                    ) : (
                        <Box
                            display="flex" alignItems="center" justifyContent="center"
                            sx={{ color: '#94a3b8', fontSize: '0.82rem', py: 6 }}
                        >
                            데이터를 불러오는 중...
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default OrgChartDialog;
